/**
 * THE BINARY WIRE FORMAT — the encoder half of the seam `codec.ts` has held open since the
 * protocol was written.
 *
 * Replication is the bottleneck by a factor of 40-70 (see `docs/architecture.md`), so the frame
 * is the tightest budget in the game. Measured on a 26-entity view frame, JSON spent 2 398 bytes
 * — 92 B per entity — on payloads that are an id, two coordinates and a mass. Quantising the
 * numbers (`constants/wire.ts`) took ~22% off, and what it left behind was not digits: it was
 * the punctuation. A steady-state delta frame of ~930 B is mostly `{"id":"`, `","x":`, `,"mass":`
 * — repeated key names — and entity ids. Those are what this file deletes.
 *
 * Three decisions worth reading before changing anything here.
 *
 * ## 1. Keys cost nothing because there are none
 *
 * Every message kind has a fixed field order, so a field's *position* in the byte stream is its
 * name. That is the bulk of the win and it is why `PROTOCOL_VERSION` has to be bumped for any
 * shape change: an old peer would not fail to find a key, it would read the next field.
 *
 * ## 2. Ids are encoded structurally, not interned
 *
 * `EntityId` is a string, and it is the single largest remaining per-entity cost — a
 * length-prefixed `"p12#3"` twenty times a second for every entity in every viewer's interest
 * set is a third of the budget this card is trying to free.
 *
 * The obvious answer is a per-connection dictionary: mint a small integer the first time a
 * string is sent, send the mapping once, send the integer thereafter. It was rejected, and the
 * reason is the seam rather than the idea. `ProtocolCodec.encode(message)` takes a message and
 * nothing else — no connection, no session — because that is what lets one encoded leaderboard
 * frame be handed to every socket in a room (`broadcast.ts`) and what lets `setProtocolCodec` be
 * a one-line swap with no call site moving. An interning codec needs per-connection state, which
 * means either threading a session through every call site, or a global dictionary that is wrong
 * the moment two rooms share a process. It also needs a retirement rule, and a retirement rule
 * is a desync waiting to happen: the sender frees a slot the receiver still has cached, or vice
 * versa, and the frame after that decodes as a different entity rather than failing.
 *
 * So the ids are encoded structurally instead. They are not arbitrary strings — every one of
 * them is minted by code in this repo from a tiny grammar:
 *
 *     p<n>              player, from `BroadcastRegistry.nextPlayerId`
 *     bot:<n>           player, from `RoomSim.addBot`
 *     f<n>              pellet          s<n>  spike          d<n>  duel   u<n>  power-up orb
 *     r<n>              projectile — a rocket, a mine or a grapple head
 *     <playerId>#<n>    cell, from `RoomSim.splitCell`
 *
 * A tag byte names the shape and a varint carries the number, so an id is two bytes instead of
 * seven or eight — and a cell id inside a player frame is *two* bytes rather than the eight its
 * owner's name would cost again, because the owner is the player being encoded and the cell
 * borrows it (`ID_CELL_OF`).
 *
 * THE RETIREMENT RULE, since the card asks for one written down: **there is nothing to retire.**
 * The mapping is a pure function of the string, computed on both sides, held nowhere. A
 * reconnect, a second room in the same process, a viewer who has never seen a player before —
 * all decode identically, because there is no accumulated state to be out of step with.
 *
 * Anything that does not fit the grammar falls back to `ID_RAW`, a length-prefixed string, and
 * the shaped forms are only used when re-rendering them reproduces the original string exactly
 * (`p007` is raw, not `ID_PLAYER 7`). So the id encoding is lossless for every string, and a
 * future id scheme costs bytes rather than correctness.
 *
 * ## 3. What is lossy is exactly what `constants/wire.ts` already declared, and nothing else
 *
 * Positions and cell masses are integers by the time a frame is built — the producers round them
 * through `quantisePosition` / `quantiseMass` — so they go out as varints. Every other number
 * (a heading, a leaderboard mass, a death mass, the room's dimensions) is a float64 and survives
 * bit for bit. The codec introduces no precision decision of its own; `binary-codec.test.ts`
 * pins that, and `broadcast.test.ts`-adjacent producers are asserted to round before they get
 * here rather than being trusted to.
 *
 * ## Decoding is codec-independent on purpose
 *
 * `decodeFrame` sniffs the frame — a text frame, or one whose first byte is `{`, is JSON;
 * anything else is binary — and both `jsonCodec` and `binaryCodec` decode through it. Only the
 * *encoder* is switched by `PROTOCOL_CODEC`. That is what makes the debug flag usable: an
 * operator can drop one process to JSON at 3am without the peer that is still speaking binary
 * becoming undecodable. A peer too old to have this file at all is refused by the
 * `PROTOCOL_VERSION` handshake, loudly, before a byte of world state is exchanged.
 */

import type { ProtocolCodec, WireData } from './codec.js';
import {
  type ClientMessage,
  PROTOCOL_ERROR_CODES,
  type ProtocolErrorCode,
  type ServerMessage,
} from './messages.js';
import type {
  CellSnapshot,
  DuelSnapshot,
  GameNotice,
  InventorySlotSnapshot,
  LeaderboardEntry,
  LeaderboardSelf,
  NoticeActor,
  NoticeKind,
  PelletSnapshot,
  PlayerCountry,
  PlayerInventory,
  PlayerSkin,
  PlayerSnapshot,
  PowerUpSnapshot,
  PrizeShardSnapshot,
  StarSnapshot,
  ProjectileSnapshot,
  RoomInfo,
  RosterEntry,
  SayingSnapshot,
  EntityId,
  MergeSnapshot,
  SpikeSnapshot,
  VoteOption,
  VoteResultSnapshot,
  VoteSelfSnapshot,
} from '../types/index.js';

/**
 * The platform globals this file leans on, declared locally for the same reason `codec.ts`
 * declares `TextDecoder`: pulling in the DOM or Node lib would let genuinely browser- or
 * Node-only APIs leak into a package that has to run in both. UTF-8 is written by hand (see
 * `writeUtf8`), so only the decode direction needs one.
 */
declare const TextDecoder: {
  new (): { decode(input: ArrayBuffer | Uint8Array): string };
};

const textDecoder = new TextDecoder();

export function toText(data: WireData): string {
  if (typeof data === 'string') return data;
  return textDecoder.decode(data);
}

// ---------------------------------------------------------------------------
// Frame kinds. One byte, shared across both directions — `parseClientMessage` /
// `parseServerMessage` are what decide which half of the protocol a frame was allowed to be, and
// they do it with the zod union they always did.
// ---------------------------------------------------------------------------

const KIND_HELLO = 0;
const KIND_INPUT = 1;
const KIND_PING = 2;
const KIND_WELCOME = 3;
const KIND_SNAPSHOT = 4;
const KIND_DELTA = 5;
const KIND_LEADERBOARD = 6;
const KIND_DIED = 7;
const KIND_PONG = 8;
const KIND_ERROR = 9;
const KIND_ROSTER = 10;
const KIND_NOTICE = 11;
const KIND_VOTE = 12;
const KIND_MATCH = 13;
/** Anything at or above this is not a frame this build can read. Also the JSON sniff's ceiling:
 * `{` is 0x7B, so a binary frame can never be mistaken for a JSON one. */
const KIND_COUNT = 14;

const JSON_OPEN_BRACE = 0x7b;

// Id shapes — see the header note. The tag is a byte of its own rather than packed into a
// length, because an id is followed by a varint whose width already varies.
const ID_RAW = 0;
const ID_PLAYER = 1;
const ID_BOT = 2;
const ID_PELLET = 3;
const ID_SPIKE = 4;
const ID_DUEL = 5;
/** `<owner>#<n>` where the owner is itself an id, written inline. */
const ID_CELL = 6;
/** `<context>#<n>`: the owner is whatever entity is being encoded around this one. */
const ID_CELL_OF = 7;
/** `u<n>`, from `RoomSim.spawnPowerUp`. */
const ID_POWERUP = 8;
/** `r<n>`, from `RoomSim.spawnProjectile`. */
const ID_PROJECTILE = 9;
/** `z<n>`, from `RoomSim.spawnPrizeShards`. `s` was the spike's, so a shard took the last letter. */
const ID_SHARD = 10;
/** `v<n>`, from `RoomSim.openVote`. */
const ID_VOTE = 11;
/**
 * `*<n>`, from `RoomSim.spawnStar`.
 *
 * The one id in this repo whose prefix is not a letter, and deliberately: every letter that could
 * stand for a star was already spoken for (`s` is the spike's, `z` the shard's, `p` a player's),
 * and an arbitrary spare letter would be a shape nobody could read. A star id starts with a star.
 * Nothing downstream parses an id's shape — this file is the only place that does — so the
 * character is free.
 */
const ID_STAR = 12;

const CHAR_STAR = 42;
const CHAR_HASH = 35;
const CHAR_ZERO = 48;
const CHAR_NINE = 57;
const CHAR_B = 98;
const CHAR_D = 100;
const CHAR_F = 102;
const CHAR_P = 112;
const CHAR_R = 114;
const CHAR_S = 115;
const CHAR_U = 117;
const CHAR_V = 118;
const CHAR_Z = 122;

/** Longest run of digits an id's numeric tail may have. Well inside a safe integer, and no id
 * this repo mints is remotely near it — the ceiling is here so a hostile string cannot make the
 * tail parser build a number that no longer round-trips through `String(n)`. */
const MAX_ID_DIGITS = 15;

/**
 * The star outcomes, in the order their index is written — DUEL_PHASES' and NOTICE_KINDS'
 * arrangement and for their reason: what this file needs is a stable *ordering* to index into,
 * which is a property of the wire format rather than of the type. `binary-codec.test.ts` pins that
 * every member of `StarOutcome` is in here.
 */
const STAR_OUTCOMES = ['auto', 'won', 'lost'] as const;
/** "No star opened this vote", which is the ordinary value. */
const STAR_OUTCOME_NONE = 0xff;

const DUEL_PHASES = ['collecting', 'revealed'] as const;
/**
 * The notice kinds, in the order their index is written. Held here as a tuple rather than
 * imported, exactly as the two duel enumerations above it are: what this file needs is a stable
 * *ordering* to index into, which is a property of the wire format rather than of the type, and
 * appending to it is a `PROTOCOL_VERSION` bump either way. `binary-codec.test.ts` pins that every
 * member of `NoticeKind` is in here.
 */
const NOTICE_KINDS = [
  'powerup_taken',
  'powerup_used',
  'shattered',
  'ate_player',
  // APPENDED, never inserted: the index is the wire format, so a kind added anywhere but the end
  // would silently re-label every notice a peer one build behind decodes.
  'star_taken',
  // Nothing raises one yet — `collectNotices` has no branch for the event behind it — so this
  // index is a word no frame this build sends can carry. See `NoticeKind` for why that is what
  // held `PROTOCOL_VERSION` still, and where the bump for it is owed.
  'round_won',
  // Appended, on the rule above: a dead player's saying, which is the one kind that carries words
  // of its own. See `GameNotice.text`.
  'said',
] as const;
const DUEL_OUTCOMES = ['a', 'b', 'draw'] as const;
/**
 * The duel outcome reasons, in the order their index is written — `DUEL_OUTCOMES`' arrangement
 * and for its reason: a stable ordering to index into is a property of the wire format rather
 * than of the type. APPENDED, never inserted: the index *is* the format, so a reason added
 * anywhere but the end would silently re-label every duel a peer one build behind decodes.
 * `binary-codec.test.ts` pins that every member of `DuelOutcomeReason` is in here.
 */
const DUEL_OUTCOME_REASONS = ['grabbed_first', 'early_grab', 'burst'] as const;

/** "This game had only one way to end", which is every game that simply compares two picks. */
const DUEL_OUTCOME_REASON_NONE = 0xff;

/**
 * The byte that means "this duel's game has no pot", in `DUEL_OUTCOMES`' own spelling one field
 * along: a sentinel rather than a flag byte plus a value, because a pot is a whole percentage
 * point and 0..100 leaves 0xff free. One byte per duel, which is what a channel that is null on
 * every frame this build can send ought to cost.
 */
const DUEL_POT_ABSENT = 0xff;

/**
 * The deathmatch phases, in the order their index is written — `DUEL_PHASES`' arrangement one
 * frame along. APPENDED, never inserted: the index *is* the format.
 *
 * Unlike `DUEL_PHASES`, this one **degrades** rather than decoding to `undefined` and letting the
 * schema refuse the frame; `readMatchPhase` is where that is argued.
 * `binary-codec.test.ts` pins that every member of `MatchPhase` is in here.
 */
const MATCH_PHASES = ['waiting', 'live', 'ended'] as const;

/**
 * The room modes, in the order their index is written, **biased by one so that zero is "this
 * frame did not say"** — `selectSlot`'s spelling, which is what fits three states into the one
 * byte a presence flag alone would have cost.
 *
 * Held here rather than imported from `constants/room-modes.ts`, exactly as the enumerations
 * above it are: that list's order belongs to the console (its own comment says `classic` leads it
 * because it is the default) and is free to be re-sorted, where this one *is* the wire format and
 * may only ever be appended to. `binary-codec.test.ts` pins that every mode the schema accepts is
 * in here, which is what stops the two drifting apart in the one direction that would matter.
 */
const ROOM_MODES = ['classic', 'deathmatch', 'teams', 'zombie'] as const;

/**
 * The two sides, as the wire writes them.
 *
 * Held here rather than imported from `constants/teams.ts`, exactly as `ROOM_MODES` above it is
 * and for its reason: that list's order belongs to the console and a scoreboard, where this one
 * IS the wire format. Two entries, and `TeamId` is why it will stay two.
 */
const TEAM_IDS = ['red', 'blue'] as const;

/** Set when a `ProtocolErrorCode` is not one this build knows; the string follows verbatim. */
const ERROR_CODE_RAW = 0xff;

// ---------------------------------------------------------------------------
// The write buffer
// ---------------------------------------------------------------------------

/**
 * Comfortably over the largest frame the game produces (a full baseline snapshot of
 * INTEREST_MAX_ENTITIES plus every spike in the room), so the growth path below is a safety net
 * rather than something a running server exercises.
 */
const INITIAL_CAPACITY = 16 * 1024;

let buffer = new Uint8Array(INITIAL_CAPACITY);
let view = new DataView(buffer.buffer);
let cursor = 0;

function ensure(extra: number): void {
  const needed = cursor + extra;
  if (needed <= buffer.length) return;
  let capacity = buffer.length;
  while (capacity < needed) capacity *= 2;
  const grown = new Uint8Array(capacity);
  grown.set(buffer.subarray(0, cursor));
  buffer = grown;
  view = new DataView(grown.buffer);
}

function writeU8(value: number): void {
  ensure(1);
  buffer[cursor] = value;
  cursor += 1;
}

/**
 * LEB128, written with division rather than shifts: `>>> 7` is a 32-bit operation and epoch
 * milliseconds are 41 bits, so the shift form would silently truncate every timestamp on the
 * wire.
 *
 * Rounds and floors at zero rather than trusting the caller. Positions and masses arrive already
 * quantised (`constants/wire.ts`) and `binary-codec.test.ts` asserts that the producers do it, so
 * this guard never fires in the game — but a `DataView`-style truncation of a stray float would
 * be a value that decodes to something *else*, which is a silent desync rather than a loud one.
 */
function writeVarUint(value: number): void {
  let v = Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
  ensure(10);
  while (v >= 0x80) {
    buffer[cursor] = (v % 0x80) | 0x80;
    cursor += 1;
    v = Math.floor(v / 0x80);
  }
  buffer[cursor] = v;
  cursor += 1;
}

/** Zigzag, so a coordinate a hair outside the world costs two bytes rather than ten. */
function writeVarInt(value: number): void {
  const n = Number.isFinite(value) ? Math.round(value) : 0;
  writeVarUint(n >= 0 ? n * 2 : n * -2 - 1);
}

function writeF64(value: number): void {
  ensure(8);
  view.setFloat64(cursor, value, true);
  cursor += 8;
}

function writeBool(value: boolean): void {
  writeU8(value ? 1 : 0);
}

/**
 * UTF-8 length of a string, without encoding it. Needed because the byte length is a prefix and
 * therefore has to be written before the bytes; the alternative — encoding into a scratch array
 * and copying — would allocate per string per frame.
 */
function utf8Length(text: string): number {
  let total = 0;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 0x80) {
      total += 1;
    } else if (code < 0x800) {
      total += 2;
    } else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        total += 4;
        i += 1;
        continue;
      }
      total += 3;
    } else {
      total += 3;
    }
  }
  return total;
}

/**
 * UTF-8 by hand rather than through `TextEncoder`.
 *
 * `encodeInto` would need a `subarray` view per call, which is an allocation per string per
 * frame on the fan-out path — the one thing `broadcast.ts` is written to avoid. A lone surrogate
 * becomes U+FFFD, which is exactly what `TextEncoder` does with one, so a frame written here and
 * read by anything else agrees.
 */
function writeUtf8(text: string): void {
  for (let i = 0; i < text.length; i += 1) {
    let code = text.charCodeAt(i);
    if (code < 0x80) {
      buffer[cursor] = code;
      cursor += 1;
      continue;
    }
    if (code < 0x800) {
      buffer[cursor] = 0xc0 | (code >> 6);
      buffer[cursor + 1] = 0x80 | (code & 0x3f);
      cursor += 2;
      continue;
    }
    if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        const point = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00);
        buffer[cursor] = 0xf0 | (point >> 18);
        buffer[cursor + 1] = 0x80 | ((point >> 12) & 0x3f);
        buffer[cursor + 2] = 0x80 | ((point >> 6) & 0x3f);
        buffer[cursor + 3] = 0x80 | (point & 0x3f);
        cursor += 4;
        i += 1;
        continue;
      }
    }
    if (code >= 0xd800 && code <= 0xdfff) code = 0xfffd;
    buffer[cursor] = 0xe0 | (code >> 12);
    buffer[cursor + 1] = 0x80 | ((code >> 6) & 0x3f);
    buffer[cursor + 2] = 0x80 | (code & 0x3f);
    cursor += 3;
  }
}

function writeString(text: string): void {
  const bytes = utf8Length(text);
  writeVarUint(bytes);
  ensure(bytes);
  writeUtf8(text);
}

// ---------------------------------------------------------------------------
// Ids
// ---------------------------------------------------------------------------

/**
 * The numeric tail of an id, or -1 if what follows `from` is not one.
 *
 * Canonical only: a leading zero, an empty tail, a non-digit or an absurd run of digits all fail,
 * because the shaped form has to render back to the *same string* it came from. Everything that
 * fails here just falls through to `ID_RAW`.
 */
function idTail(id: string, from: number): number {
  const length = id.length;
  if (from >= length || length - from > MAX_ID_DIGITS) return -1;
  if (id.charCodeAt(from) === CHAR_ZERO && length - from > 1) return -1;
  let value = 0;
  for (let i = from; i < length; i += 1) {
    const code = id.charCodeAt(i);
    if (code < CHAR_ZERO || code > CHAR_NINE) return -1;
    value = value * 10 + (code - CHAR_ZERO);
  }
  return value;
}

/** `p<n>` / `bot:<n>` / `f<n>` / `s<n>` / `d<n>` / `u<n>` / `r<n>` / `z<n>` / `v<n>` / `*<n>`, or
 * false. */
function writePrefixedId(id: string): boolean {
  const head = id.charCodeAt(0);
  let tag = -1;
  let from = 1;
  if (head === CHAR_P) tag = ID_PLAYER;
  else if (head === CHAR_F) tag = ID_PELLET;
  else if (head === CHAR_S) tag = ID_SPIKE;
  else if (head === CHAR_D) tag = ID_DUEL;
  else if (head === CHAR_U) tag = ID_POWERUP;
  else if (head === CHAR_R) tag = ID_PROJECTILE;
  else if (head === CHAR_Z) tag = ID_SHARD;
  else if (head === CHAR_V) tag = ID_VOTE;
  else if (head === CHAR_STAR) tag = ID_STAR;
  else if (head === CHAR_B && id.startsWith('bot:')) {
    tag = ID_BOT;
    from = 4;
  } else return false;

  const value = idTail(id, from);
  if (value < 0) return false;
  writeU8(tag);
  writeVarUint(value);
  return true;
}

/**
 * One entity id.
 *
 * `context` is the id of the entity this one hangs off — the player a cell belongs to. When it
 * matches, the cell costs a tag and a varint and the owner is not repeated at all; the reader
 * has the same context in hand, because the field order puts the owner first.
 */
function writeId(id: string, context?: string): void {
  if (
    context !== undefined &&
    id.length > context.length + 1 &&
    id.charCodeAt(context.length) === CHAR_HASH &&
    id.startsWith(context)
  ) {
    const value = idTail(id, context.length + 1);
    if (value >= 0) {
      writeU8(ID_CELL_OF);
      writeVarUint(value);
      return;
    }
  }

  const hash = id.indexOf('#');
  if (hash > 0) {
    const value = idTail(id, hash + 1);
    if (value >= 0) {
      writeU8(ID_CELL);
      writeId(id.slice(0, hash));
      writeVarUint(value);
      return;
    }
  }

  if (id.length > 0 && writePrefixedId(id)) return;

  writeU8(ID_RAW);
  writeString(id);
}

// ---------------------------------------------------------------------------
// Encoding
// ---------------------------------------------------------------------------

function writeCell(cell: CellSnapshot, owner: string): void {
  writeId(cell.id, owner);
  writeVarInt(cell.x);
  writeVarInt(cell.y);
  writeVarUint(cell.mass);
}

function writePlayer(player: PlayerSnapshot): void {
  writeId(player.id);
  writeString(player.name);
  writeVarUint(player.hue);
  writeVarUint(player.cells.length);
  for (const cell of player.cells) writeCell(cell, player.id);
}

function writePlayers(players: readonly PlayerSnapshot[]): void {
  writeVarUint(players.length);
  for (const player of players) writePlayer(player);
}

function writePellets(pellets: readonly PelletSnapshot[]): void {
  writeVarUint(pellets.length);
  for (const pellet of pellets) {
    writeId(pellet.id);
    writeVarInt(pellet.x);
    writeVarInt(pellet.y);
    writeVarUint(pellet.hue);
    // Already a step in [0, PELLET_SIZE_STEPS] — PELLET_SIZE_STEPS is 255, so it is a byte.
    writeU8(pellet.size);
  }
}

/**
 * The room's orbs. Positions are quantised by their producer like every other coordinate, so
 * they go out as zigzag varints; `kind` is a short registry id and rides as a string, because
 * indexing it into `POWER_UPS` would make the wire depend on the *order* of a registry two
 * builds might have written down differently.
 */
function writePowerUps(powerUps: readonly PowerUpSnapshot[]): void {
  writeVarUint(powerUps.length);
  for (const orb of powerUps) {
    writeId(orb.id);
    writeString(orb.kind);
    writeVarInt(orb.x);
    writeVarInt(orb.y);
  }
}

/**
 * The viewer's own inventory — the count, then the selected index, then one slot each.
 *
 * **THE INDEX IS WRITTEN ONLY WHEN THERE IS A SLOT FOR IT TO POINT AT**, so a player carrying
 * nothing costs exactly what `heldPowerUp` did before there was an inventory at all: one zero
 * byte, on every frame, for every viewer in every room. That is the whole reason the count leads
 * — an empty list can be recognised before anything else is read — and it is what lets this ride
 * every frame rather than the `powerup` divisor.
 *
 * A slot is a short registry id, two small varints and a flag byte. `ticksLeft` is relative
 * rather than a deadline, so it needs no agreement about the server's tick count, and the pair is
 * written unconditionally rather than switched on the use model: one of them is always zero, and
 * a varint zero is one byte — cheaper than the presence bit that would decide which to write.
 *
 * `running` is its own byte for the same arithmetic read the other way: it cannot be folded into
 * `ticksLeft` because an unstarted window deliberately reports its FULL duration there (so a chip
 * reads "10s" before it is fired), which makes the two indistinguishable by value.
 */
function writeInventory(inventory: PlayerInventory): void {
  writeVarUint(inventory.slots.length);
  if (inventory.slots.length === 0) return;
  writeVarUint(inventory.selected);
  for (const slot of inventory.slots) {
    writeString(slot.kind);
    writeVarUint(slot.ticksLeft);
    writeVarUint(slot.charges);
    writeU8(slot.running ? 1 : 0);
  }
}

/** One presence byte, then the list — `null` is "no news this frame", `[]` is "none left". */
function writeOptionalSpikes(spikes: readonly SpikeSnapshot[] | null): void {
  if (spikes === null) {
    writeU8(0);
    return;
  }
  writeU8(1);
  writeSpikes(spikes);
}

/** One presence byte, then the list — `null` is "no news this frame", `[]` is "none left". */
function writeOptionalPowerUps(powerUps: readonly PowerUpSnapshot[] | null): void {
  if (powerUps === null) {
    writeU8(0);
    return;
  }
  writeU8(1);
  writePowerUps(powerUps);
}

/**
 * The room's prize shards: an id and a place each, and **nothing else**.
 *
 * The cheapest list on the frame after the id lists, and the missing field is the feature: what a
 * shard is worth never leaves the simulation (`PrizeShardState.mass`), so there is no mass here to
 * quantise and no way for a client to read the pot off the field. Positions are quantised by
 * their producer like every other coordinate, so they go out as zigzag varints.
 */
function writePrizeShards(shards: readonly PrizeShardSnapshot[]): void {
  writeVarUint(shards.length);
  for (const shard of shards) {
    writeId(shard.id);
    writeVarInt(shard.x);
    writeVarInt(shard.y);
  }
}

/** `writeOptionalPowerUps`, for the shards — a shard does not move, so it rides the same tier. */
function writeOptionalPrizeShards(shards: readonly PrizeShardSnapshot[] | null): void {
  if (shards === null) {
    writeU8(0);
    return;
  }
  writeU8(1);
  writePrizeShards(shards);
}

/**
 * The room's stars: an id and a place each, `writePrizeShards`' body exactly.
 *
 * At most MAX_STARS_PER_ROOM of them and usually none, so a room that has never seen one pays the
 * single zero byte the count costs. What taking one is *worth* is not here and could not be —
 * the payout is three times the taker's own mass out of a pot that does not exist yet.
 */
function writeStars(stars: readonly StarSnapshot[]): void {
  writeVarUint(stars.length);
  for (const star of stars) {
    writeId(star.id);
    writeVarInt(star.x);
    writeVarInt(star.y);
  }
}

/** `writeOptionalPrizeShards`, for the stars — a star does not move either. */
function writeOptionalStars(stars: readonly StarSnapshot[] | null): void {
  if (stars === null) {
    writeU8(0);
    return;
  }
  writeU8(1);
  writeStars(stars);
}

/**
 * The room's projectiles. Same shape as an orb's plus the owner and the armed bit — the owner so
 * a grapple's chain has somewhere to be drawn from, the bit because an unarmed mine is scenery
 * and an armed one is a wall.
 */
function writeProjectiles(projectiles: readonly ProjectileSnapshot[]): void {
  writeVarUint(projectiles.length);
  for (const shot of projectiles) {
    writeId(shot.id);
    writeString(shot.kind);
    writeId(shot.ownerId);
    writeVarInt(shot.x);
    writeVarInt(shot.y);
    writeU8(shot.armed ? 1 : 0);
  }
}

/**
 * The speech bubbles on this frame: the speaker's id, the resolved words, and the tick the
 * bubble stops applying.
 *
 * A length-prefixed string per bubble, which is the one place this codec spends real bytes on
 * text — and it spends them once. A bubble is named on the frame it starts and never again (see
 * `serverDeltaSchema.sayings`), so the steady-state cost of the whole feature to a frame is the
 * single zero this writes when the list is empty.
 */
function writeSayings(sayings: readonly SayingSnapshot[]): void {
  writeVarUint(sayings.length);
  for (const saying of sayings) {
    writeId(saying.id);
    writeString(saying.text);
    writeVarUint(saying.endsAtTick);
  }
}

/**
 * The merged pairs on this frame: an id, the driver, the members and their names, and the tick it
 * comes apart on.
 *
 * The one list on the frame that carries display names besides `sayings`, and for the same reason
 * — they are drawn on a blob, and most of the people reading them are looking at somebody else's
 * merge. Empty in every room that is not a `teams` room, which makes the steady-state cost of the
 * whole feature the single zero this writes.
 */
function writeMerges(merges: readonly MergeSnapshot[]): void {
  writeVarUint(merges.length);
  for (const merge of merges) {
    writeId(merge.id);
    writeId(merge.controllerId);
    writeVarUint(merge.memberIds.length);
    for (const id of merge.memberIds) writeId(id);
    for (const name of merge.names) writeString(name);
    writeVarUint(merge.endsAtTick);
  }
}

function readMerges(): MergeSnapshot[] {
  const count = readCount();
  const merges: MergeSnapshot[] = [];
  for (let i = 0; i < count; i += 1) {
    const id = readId();
    const controllerId = readId();
    const members = readCount();
    const memberIds: EntityId[] = [];
    for (let k = 0; k < members; k += 1) memberIds.push(readId());
    const names: string[] = [];
    for (let k = 0; k < members; k += 1) names.push(readString());
    merges.push({ id, controllerId, memberIds, names, endsAtTick: readVarUint() });
  }
  return merges;
}

function writeSpikes(spikes: readonly SpikeSnapshot[]): void {
  writeVarUint(spikes.length);
  for (const spike of spikes) {
    writeId(spike.id);
    writeVarInt(spike.x);
    writeVarInt(spike.y);
    // How many ejections it has swallowed — one byte, and zero for every spike in a room nobody
    // has fed. The size itself is `spikeRadiusFor(fed)` and is not on the wire.
    writeVarUint(spike.fed);
  }
}

/**
 * A duel.
 *
 * Note what is NOT here: any decision about whether a choice may be sent. `aChoice` and
 * `bChoice` are written exactly as `toDuelSnapshot` left them, null included. Choice secrecy has
 * one home and this is not it — a codec that suppressed a choice would be a second copy of that
 * rule, and the copy that was not updated is the one that leaks.
 */
function writeDuels(duels: readonly DuelSnapshot[]): void {
  writeVarUint(duels.length);
  for (const duel of duels) {
    writeId(duel.id);
    writeString(duel.gameId);
    writeU8(DUEL_PHASES.indexOf(duel.phase));
    writeId(duel.aId);
    writeId(duel.bId);
    writeBool(duel.aLocked);
    writeBool(duel.bLocked);
    writeOptionalString(duel.aChoice);
    writeOptionalString(duel.bChoice);
    if (duel.outcome === null) writeU8(0xff);
    else writeU8(DUEL_OUTCOMES.indexOf(duel.outcome));
    // Why it ended that way, as an index, or the sentinel. One byte, and there is deliberately
    // nothing beside it: the reason says a grab was early and never how early, because the tick a
    // pot arms on never reaches a `DuelSnapshot` at all. Same containment as the bust point.
    writeU8(
      duel.outcomeReason === null
        ? DUEL_OUTCOME_REASON_NONE
        : DUEL_OUTCOME_REASONS.indexOf(duel.outcomeReason),
    );
    writeVarUint(duel.endsAtTick);
    // A float rather than a varint, and never rounded: a stake is a mass, and every other mass on
    // this wire that is not a *cell's* is a float64 (`leaderboardEntry.mass`, `died.mass`). It is
    // nullable in `writeOptionalString`'s shape rather than `writeOptionalF64`'s, because null and
    // absent are different words for the same absence here and the type says null.
    writeNullableF64(duel.stake);
    // The pot's height, or the sentinel. **THE BUST POINT IS NOT WRITTEN HERE AND THERE IS
    // NOTHING TO WRITE**: it never reaches a `DuelSnapshot`, which is the containment rather than
    // a decision this codec is making — a codec that suppressed a value would be a second copy of
    // a rule, and the copy that was not updated is the one that leaks.
    writeU8(duel.pot === null ? DUEL_POT_ABSENT : duel.pot);
  }
}

/** One flag byte, then the value if it was there. `absent` and `0` are different values. */
function writeOptionalF64(value: number | undefined): void {
  if (value === undefined) {
    writeU8(0);
    return;
  }
  writeU8(1);
  writeF64(value);
}

/** One flag byte, then the value if it was there. Null is `writeOptionalString`'s spelling. */
function writeNullableF64(value: number | null): void {
  if (value === null) {
    writeU8(0);
    return;
  }
  writeU8(1);
  writeF64(value);
}

function writeOptionalString(value: string | null | undefined): void {
  if (value === null || value === undefined) {
    writeU8(0);
    return;
  }
  writeU8(1);
  writeString(value);
}

function writeSkins(skins: readonly PlayerSkin[]): void {
  writeVarUint(skins.length);
  for (const skin of skins) {
    writeId(skin.id);
    writeString(skin.url);
  }
}

/**
 * A country code as two raw bytes, no length prefix — every one of them is exactly two ASCII
 * capitals (`countryCodeSchema` is what makes that true before a frame is ever built), so a
 * length would be a third byte spent restating a constant.
 */
function writeCountries(countries: readonly PlayerCountry[]): void {
  writeVarUint(countries.length);
  for (const entry of countries) {
    writeId(entry.id);
    writeU8(entry.country.charCodeAt(0));
    writeU8(entry.country.charCodeAt(1));
  }
}

/** One presence byte, then the same two raw bytes `writeCountries` uses. */
function writeOptionalCountry(country: string | undefined): void {
  if (country === undefined) {
    writeU8(0);
    return;
  }
  writeU8(1);
  writeU8(country.charCodeAt(0));
  writeU8(country.charCodeAt(1));
}

function writeIds(ids: readonly string[]): void {
  writeVarUint(ids.length);
  for (const id of ids) writeId(id);
}

function writeLeaderboard(entries: readonly LeaderboardEntry[]): void {
  writeVarUint(entries.length);
  for (const entry of entries) {
    writeVarUint(entry.rank);
    writeId(entry.playerId);
    writeString(entry.name);
    // Not quantised by its producer, so not quantised here either — see the header note.
    writeF64(entry.mass);
    writeOptionalCountry(entry.country);
  }
}

/**
 * The room's roster: a count, then a shaped id, a name, one flag byte and an optional country
 * each. Nothing here is quantised because nothing here is a number — which is also what keeps a
 * 200-player roster at a few kilobytes rather than at the tens the same list of players costs on
 * a world frame.
 */
function writeRoster(entries: readonly RosterEntry[]): void {
  writeVarUint(entries.length);
  for (const entry of entries) {
    writeId(entry.id);
    writeString(entry.name);
    writeBool(entry.bot);
    // Coarse — MINIMAP_POSITION_STEP, quantised by the host — so a default room is 63 steps
    // across and a coordinate is one varint byte. See `RosterEntry.x`.
    writeVarInt(entry.x);
    writeVarInt(entry.y);
    writeOptionalCountry(entry.country);
  }
}

/** One actor: the resolved name, then one flag byte. No id — see `NoticeActor`. */
function writeNoticeActor(actor: NoticeActor): void {
  writeString(actor.name);
  writeBool(actor.bot);
}

/**
 * The room's event feed: a count, then a kind byte, a subject, an optional target and an optional
 * power-up id each.
 *
 * The names are the whole of what this spends bytes on, and it spends them once — a notice is
 * sent on the tick it happened and never re-stated, and there is no frame at all on a tick that
 * produced none (see `serverNoticeSchema`), so the steady-state cost of the feature to a room
 * where nothing is happening is zero rather than the empty list every other list here costs.
 */
function writeNotices(notices: readonly GameNotice[]): void {
  writeVarUint(notices.length);
  for (const notice of notices) {
    writeU8(NOTICE_KINDS.indexOf(notice.kind));
    writeNoticeActor(notice.subject);
    if (notice.target === null) writeU8(0);
    else {
      writeU8(1);
      writeNoticeActor(notice.target);
    }
    writeOptionalString(notice.powerUp);
    // The words, for `said` alone. One presence byte on every other kind, which is what keeps a
    // room's ordinary notice frame the size it was.
    writeOptionalString(notice.text);
  }
}

/** The viewer's own row: a presence byte, then the same two fields an entry carries. */
function writeLeaderboardSelf(self: LeaderboardSelf | null): void {
  if (self === null) {
    writeU8(0);
    return;
  }
  writeU8(1);
  writeVarUint(self.rank);
  writeF64(self.mass);
}

/**
 * A vote's ballot: a count, then an id and a label each. Nothing here is quantised because
 * nothing here is a number, and it is written exactly twice in a vote's life.
 */
function writeVoteOptions(options: readonly VoteOption[]): void {
  writeVarUint(options.length);
  for (const option of options) {
    writeString(option.id);
    writeString(option.label);
  }
}

/**
 * A presence byte, then the winner, the tallies and what actually moved.
 *
 * **THE ABSENT VALUE IS THE SECRECY RULE ON THE WIRE.** While a vote is open this is one zero
 * byte and there is nothing after it — no tally, no count of who has answered, nothing. That is
 * enforced here rather than described elsewhere: there is no branch in this function that could
 * write a count for an open vote, because an open vote has no result to write.
 */
function writeVoteResult(result: VoteResultSnapshot | null): void {
  if (result === null) {
    writeU8(0);
    return;
  }
  writeU8(1);
  writeOptionalString(result.winner);
  writeVarUint(result.tallies.length);
  for (const tally of result.tallies) {
    writeString(tally.optionId);
    writeVarUint(tally.votes);
  }
  writeF64(result.massMoved);
  // On the end of the result, after every field a peer already counts on. A sentinel rather than a
  // presence byte plus an index, in `DuelSnapshot.pot`'s own spelling: STAR_OUTCOMES is short and
  // there will never be a 255th entry in it.
  writeU8(
    result.starOutcome === null ? STAR_OUTCOME_NONE : STAR_OUTCOMES.indexOf(result.starOutcome),
  );
}

/**
 * The viewer's own row: a presence byte, then what they answered and what it was worth.
 *
 * `writeLeaderboardSelf`'s shape and its price — one small extra encode per viewer who has a row
 * — except that a vote pays it twice in its life rather than twice a second.
 */
function writeVoteSelf(self: VoteSelfSnapshot | null): void {
  if (self === null) {
    writeU8(0);
    return;
  }
  writeU8(1);
  writeString(self.choice);
  // Signed, and not quantised by its producer: a loser's change is negative, which is the whole
  // reason this is a float rather than one of the varints the rest of the frame is made of.
  writeF64(self.massChange);
}

function writeMessage(message: ClientMessage | ServerMessage): void {
  switch (message.t) {
    case 'hello': {
      writeU8(KIND_HELLO);
      writeVarUint(message.protocolVersion);
      writeString(message.name);
      writeOptionalString(message.ticket);
      if (message.roomId === undefined) writeU8(0);
      else {
        writeU8(1);
        writeId(message.roomId);
      }
      writeOptionalF64(message.aspect);
      return;
    }
    case 'input': {
      writeU8(KIND_INPUT);
      writeVarUint(message.seq);
      // Headings are not quantised anywhere, so they go out whole. The inbound direction is a
      // rounding error of the outbound one in bytes — one small frame per client per tick.
      writeF64(message.dirX);
      writeF64(message.dirY);
      // `boost` is optional *and* a boolean, and absent is not the same value as false: one
      // bit says whether it was there, the next says what it was.
      writeU8(message.boost === undefined ? 0 : message.boost ? 3 : 1);
      // `split` is the same shape of field and gets the same two bits. It is written as its own
      // byte rather than packed into the one above so that the two stay independently readable
      // in a dump — this is the direction that is not the bottleneck (replication is, by 40-70x),
      // and one byte per client per tick is not worth spending legibility on.
      writeU8(message.split === undefined ? 0 : message.split ? 3 : 1);
      // `eject`, the third of them, on the same two bits again. Written after `split` because it
      // was added after it: field order is the field name here, so a new flag goes on the end of
      // the frame it belongs to and never between two that a peer already counts on.
      writeU8(message.eject === undefined ? 0 : message.eject ? 3 : 1);
      // `usePowerUp`, the fourth of them, on the same two bits again and on the end of the frame
      // for the same reason `eject` was: field order is the field name here, so a new flag goes
      // after every one a peer already counts on and never between two of them.
      writeU8(message.usePowerUp === undefined ? 0 : message.usePowerUp ? 3 : 1);
      writeOptionalString(message.duelChoice);
      // Sent only on the frames where it changed, so this is one byte on every other one.
      writeOptionalF64(message.aspect);
      // `saying`, the fifth action, written on the very end of the frame: field order is the
      // field name here, so a new field goes after every one a peer already counts on and never
      // between two of them. An id rather than a flag, and an id rather than text — see
      // `clientInputSchema.saying`.
      writeOptionalString(message.saying);
      // The spectator camera, on the end of the frame for the reason `saying` is. Two presence
      // bytes on a player's frame, which is what every other optional field on it already costs,
      // and the only frames that ever carry the pair are the ones a spectator sends.
      writeOptionalF64(message.camX);
      writeOptionalF64(message.camY);
      // `selectSlot`, the sixth action, on the very end of the frame: field order is the field
      // name here, so a new field goes after every one a peer already counts on. One varint
      // rather than a presence byte and a value — the index is biased by one and zero is
      // "absent", which fits every legal slot of a POWER_UP_INVENTORY_SIZE inventory in the one
      // byte an absent field would have cost on its own.
      writeVarUint(message.selectSlot === undefined ? 0 : message.selectSlot + 1);
      // `voteAnswer`, the seventh action, on the very end of the frame: field order is the field
      // name here, so a new field goes after every one a peer already counts on and never between
      // two of them. An id rather than an index — see `clientInputSchema.voteAnswer` — so it is
      // `writeOptionalString`, one byte on every frame that is not answering a vote.
      writeOptionalString(message.voteAnswer);
      // `sprint`, on the very end of the frame after every field a peer already counts on — the
      // tradition every field added here follows. The same two bits `boost`, `split`, `eject` and
      // `usePowerUp` get, because it is the same shape of field on the wire; what is different is
      // what a client does with it, and that is not something a codec can express. It is a HELD
      // LEVEL, so a sprinting client spends this byte on every frame rather than on one of them
      // — see `clientInputSchema.sprint`.
      writeU8(message.sprint === undefined ? 0 : message.sprint ? 3 : 1);
      // `endRound`, on the very end of the frame after every field a peer already counts on —
      // the tradition every field added here follows. The same two bits the five booleans above
      // it get; it is an EDGE, so the byte is 0 on every frame that is not asking.
      writeU8(message.endRound === undefined ? 0 : message.endRound ? 3 : 1);
      return;
    }
    case 'ping': {
      writeU8(KIND_PING);
      writeVarUint(message.clientTime);
      return;
    }
    case 'welcome': {
      writeU8(KIND_WELCOME);
      writeVarUint(message.protocolVersion);
      writeId(message.playerId);
      writeId(message.room.id);
      // The room's name, once per join. On the end of the room block rather than of the frame,
      // because `RoomInfo` is read back as a unit — and this frame is one per socket for the
      // life of a match, so a length-prefixed string on it costs nothing worth counting.
      writeString(message.room.name);
      writeF64(message.room.worldWidth);
      writeF64(message.room.worldHeight);
      writeF64(message.room.tickRateHz);
      writeVarUint(message.room.maxPlayers);
      writeVarUint(message.room.playerCount);
      // The room's mode, on the end of the room block rather than of the frame — the name's own
      // placement one field along, and for its reason: `RoomInfo` is read back as a unit. One
      // varint, biased by one so that absent, `classic` and `deathmatch` all fit in the single
      // byte an optional field would have cost on its own, and so a `welcome` costs a deathmatch
      // room exactly what it costs a classic one.
      writeVarUint(message.room.mode === undefined ? 0 : ROOM_MODES.indexOf(message.room.mode) + 1);
      writeVarUint(message.serverTime);
      // On the end of the frame, after every field a peer already counts on. One byte, once per
      // socket for the life of a match.
      writeU8(message.spectator ? 1 : 0);
      return;
    }
    case 'snapshot': {
      writeU8(KIND_SNAPSHOT);
      writeVarUint(message.tick);
      writeVarUint(message.ackSeq);
      writePlayers(message.players);
      writePellets(message.pellets);
      writeSpikes(message.spikes);
      writeDuels(message.duels);
      writeSkins(message.skins);
      writeIds(message.bots);
      writeCountries(message.countries);
      writePowerUps(message.powerUps);
      writeInventory(message.inventory);
      writeProjectiles(message.projectiles);
      writeSayings(message.sayings);
      writeMerges(message.merges);
      // On the end of the frame, after every field a peer already counts on — the tradition every
      // field added to an existing frame here follows.
      writeIds(message.frozen);
      writeIds(message.slowed);
      writeIds(message.invisible);
      writeIds(message.spawnProtected);
      writePrizeShards(message.prizeShards);
      writeStars(message.stars);
      // The fifth status list and the viewer's own bar, on the end of the frame after every field
      // a peer already counts on. The bar is a whole percentage point of STAMINA_MAX — one byte,
      // because STAMINA_MAX is 100 and a varint holds it in one — so the whole of this feature
      // costs a quiet frame two bytes: a zero-length list and a full meter.
      writeIds(message.sprinting);
      writeVarUint(message.stamina);
      return;
    }
    case 'delta': {
      writeU8(KIND_DELTA);
      writeVarUint(message.tick);
      writeVarUint(message.baseTick);
      writeVarUint(message.ackSeq);
      writePlayers(message.players);
      writeIds(message.removedPlayers);
      writePellets(message.pelletsAdded);
      writeIds(message.pelletsRemoved);
      writeDuels(message.duels);
      writeSkins(message.skins);
      writeIds(message.bots);
      writeCountries(message.countries);
      writeOptionalPowerUps(message.powerUps);
      writeInventory(message.inventory);
      writeProjectiles(message.projectiles);
      writeSayings(message.sayings);
      writeMerges(message.merges);
      writeIds(message.frozen);
      writeIds(message.slowed);
      writeIds(message.invisible);
      writeIds(message.spawnProtected);
      writeOptionalPrizeShards(message.prizeShards);
      writeOptionalStars(message.stars);
      // The snapshot's pair, in the snapshot's place and for its reasons. Not nullable and not on
      // a divisor, unlike the two lines above them: a sprint is a 20 Hz fact about a moving blob
      // and the bar is the answer to "why did I stop".
      writeIds(message.sprinting);
      writeVarUint(message.stamina);
      // On the very end of the frame after every field a peer already counts on, the tradition
      // every field added here follows. Null on every frame nothing happened to the spike field,
      // which is nearly all of them — one byte. See `serverDeltaSchema.spikes`.
      writeOptionalSpikes(message.spikes);
      return;
    }
    case 'leaderboard': {
      writeU8(KIND_LEADERBOARD);
      writeVarUint(message.tick);
      writeVarUint(message.humanCount);
      writeVarUint(message.botCount);
      writeLeaderboard(message.entries);
      writeLeaderboardSelf(message.self);
      return;
    }
    case 'roster': {
      writeU8(KIND_ROSTER);
      writeVarUint(message.tick);
      writeRoster(message.entries);
      return;
    }
    case 'notice': {
      writeU8(KIND_NOTICE);
      writeVarUint(message.tick);
      writeNotices(message.notices);
      return;
    }
    case 'vote': {
      writeU8(KIND_VOTE);
      writeVarUint(message.tick);
      writeId(message.id);
      writeString(message.prompt);
      writeVoteOptions(message.options);
      writeVarUint(message.endsAtTick);
      writeVoteResult(message.result);
      writeVoteSelf(message.self);
      // On the end of the frame, after every field a peer already counts on.
      writeU8(message.star ? 1 : 0);
      return;
    }
    case 'match': {
      writeU8(KIND_MATCH);
      writeVarUint(message.tick);
      writeU8(MATCH_PHASES.indexOf(message.phase));
      // Biased by one, so that "this phase has no deadline" and tick 1 are different bytes and
      // neither costs a presence flag — `selectSlot`'s spelling, one frame along. Null rather
      // than the simulation's 0 because 0 is a legal tick; see `serverMatchSchema.endsAtTick`.
      writeVarUint(message.endsAtTick === null ? 0 : message.endsAtTick + 1);
      writeVarUint(message.round);
      writeVarUint(message.waiting);
      writeVarUint(message.capacity);
      writeVarUint(message.alive);
      // Beside `alive` rather than folded into it: they answer different questions, and this one
      // is the half of the round that has anybody's run in it. See `serverMatchSchema`.
      writeVarUint(message.humansAlive);
      writeOptionalString(message.winnerName);
      writeU8(message.winnerIsBot ? 1 : 0);
      /**
       * The scoreboard: a count and then a name, a mass and a flag each.
       *
       * Empty in every phase but `ended`, which is every frame a room sends except one per round
       * — so the ordinary cost of this field is the single zero byte the count writes.
       */
      writeVarUint(message.standings.length);
      for (const row of message.standings) {
        writeString(row.name);
        writeVarUint(row.mass);
        writeU8(row.isBot ? 1 : 0);
      }
      // The one per-viewer field, last: false on the shared payload the whole room is handed and
      // true on the one extra encode the winner's socket gets. See `serverMatchSchema.youWon`.
      writeU8(message.youWon ? 1 : 0);
      /**
       * The team scoreboard, on the very end of the frame after every field a peer already counts
       * on — the tradition every field added here follows. One presence byte and nothing else in
       * a room that is not a `teams` room, which is what keeps a deathmatch frame the fourteen
       * bytes it was. Both sides are always written when it is present, in the order they come.
       */
      if (message.teams === null) {
        writeU8(0);
      } else {
        writeU8(1);
        for (const row of message.teams) {
          writeU8(TEAM_IDS.indexOf(row.team));
          writeVarUint(row.mass);
          writeVarUint(row.alive);
        }
      }
      // The per-viewer half, biased by one so absent and either side fit in the byte a presence
      // flag alone would have cost — `selectSlot`'s spelling.
      writeVarUint(message.yourTeam === null ? 0 : TEAM_IDS.indexOf(message.yourTeam) + 1);
      return;
    }
    case 'died': {
      writeU8(KIND_DIED);
      writeVarUint(message.tick);
      if (message.killerId === null) writeU8(0);
      else {
        writeU8(1);
        writeId(message.killerId);
      }
      writeOptionalString(message.killerName);
      writeU8(message.killerIsBot ? 1 : 0);
      writeF64(message.mass);
      writeVarUint(message.survivedMs);
      return;
    }
    case 'pong': {
      writeU8(KIND_PONG);
      writeVarUint(message.clientTime);
      writeVarUint(message.serverTime);
      return;
    }
    case 'error': {
      writeU8(KIND_ERROR);
      const index = PROTOCOL_ERROR_CODES.indexOf(message.code);
      if (index < 0) {
        writeU8(ERROR_CODE_RAW);
        writeString(message.code);
      } else {
        writeU8(index);
      }
      writeString(message.message);
      return;
    }
  }
}

/**
 * Encode one frame.
 *
 * The scratch buffer above is reused for every frame of every viewer — that is the allocation
 * rule `broadcast.ts` is built on, and it is why the encoder never sizes a buffer per frame.
 * The `slice` at the end is the one allocation, and it is not optional: `ws` hands a buffer
 * straight to `socket.write`, which may keep the reference until the kernel drains, so a frame
 * that shared the scratch would be rewritten under a socket that had not sent it yet. It is a
 * copy of exactly the bytes produced — a few hundred, against the ~930-byte *string* JSON
 * allocated for the same frame.
 */
export function encodeBinary(message: ClientMessage | ServerMessage): Uint8Array {
  cursor = 0;
  writeMessage(message);
  return buffer.slice(0, cursor);
}

// ---------------------------------------------------------------------------
// Decoding
// ---------------------------------------------------------------------------

let source: Uint8Array<ArrayBufferLike> = new Uint8Array(0);
let position = 0;

/**
 * The `DataView` the float reads go through, over whichever buffer the frame arrived in. `ws`
 * hands out `Buffer`s that are views into a shared pool, so `byteOffset` is routinely non-zero —
 * reading at `position` alone would read somebody else's frame.
 */
let readerBuffer: ArrayBufferLike = new ArrayBuffer(0);
let readerView: DataView<ArrayBufferLike> = new DataView(readerBuffer);

function fail(reason: string): never {
  throw new Error(`binary protocol: ${reason}`);
}

function readU8(): number {
  if (position >= source.length) fail('frame ended mid-field');
  const value = source[position];
  position += 1;
  return value;
}

function readVarUint(): number {
  let value = 0;
  let scale = 1;
  for (let i = 0; i < 9; i += 1) {
    const byte = readU8();
    value += (byte & 0x7f) * scale;
    if ((byte & 0x80) === 0) return value;
    scale *= 0x80;
  }
  fail('varint too long');
}

function readVarInt(): number {
  const value = readVarUint();
  return value % 2 === 0 ? value / 2 : -(value + 1) / 2;
}

function readF64(): number {
  if (position + 8 > source.length) fail('frame ended mid-float');
  const value = readerView.getFloat64(source.byteOffset + position, true);
  position += 8;
  return value;
}

function readBool(): boolean {
  return readU8() !== 0;
}

function readString(): string {
  const bytes = readVarUint();
  if (position + bytes > source.length) fail('string runs past the end of the frame');
  const text = textDecoder.decode(source.subarray(position, position + bytes));
  position += bytes;
  return text;
}

function readOptionalF64(): number | undefined {
  return readU8() === 0 ? undefined : readF64();
}

function readNullableF64(): number | null {
  return readU8() === 0 ? null : readF64();
}

function readOptionalString(): string | null {
  return readU8() === 0 ? null : readString();
}

function readId(context?: string): string {
  const tag = readU8();
  switch (tag) {
    case ID_RAW:
      return readString();
    case ID_PLAYER:
      return `p${readVarUint()}`;
    case ID_BOT:
      return `bot:${readVarUint()}`;
    case ID_PELLET:
      return `f${readVarUint()}`;
    case ID_SPIKE:
      return `s${readVarUint()}`;
    case ID_DUEL:
      return `d${readVarUint()}`;
    case ID_POWERUP:
      return `u${readVarUint()}`;
    case ID_PROJECTILE:
      return `r${readVarUint()}`;
    case ID_SHARD:
      return `z${readVarUint()}`;
    case ID_VOTE:
      return `v${readVarUint()}`;
    case ID_STAR:
      return `*${readVarUint()}`;
    case ID_CELL: {
      const owner = readId();
      return `${owner}#${readVarUint()}`;
    }
    case ID_CELL_OF:
      if (context === undefined) fail('relative id outside an owner');
      return `${context}#${readVarUint()}`;
    default:
      return fail(`unknown id tag ${tag}`);
  }
}

/**
 * A list length, checked against what is left of the frame before anything is allocated for it.
 *
 * Every element costs at least one byte, so a count larger than the remaining bytes is a lie —
 * and refusing it here is what stops a forged four-byte frame from asking for a four-billion
 * element array. The read loops below then run out of bytes naturally on anything subtler.
 */
function readCount(): number {
  const count = readVarUint();
  if (count > source.length - position) fail('list longer than the frame that carries it');
  return count;
}

function readPlayers(): PlayerSnapshot[] {
  const count = readCount();
  const players: PlayerSnapshot[] = [];
  for (let i = 0; i < count; i += 1) {
    const id = readId();
    const name = readString();
    const hue = readVarUint();
    const cellCount = readCount();
    const cells: CellSnapshot[] = [];
    for (let c = 0; c < cellCount; c += 1) {
      cells.push({
        id: readId(id),
        x: readVarInt(),
        y: readVarInt(),
        mass: readVarUint(),
      });
    }
    players.push({ id, name, hue, cells });
  }
  return players;
}

function readPellets(): PelletSnapshot[] {
  const count = readCount();
  const pellets: PelletSnapshot[] = [];
  for (let i = 0; i < count; i += 1) {
    pellets.push({
      id: readId(),
      x: readVarInt(),
      y: readVarInt(),
      hue: readVarUint(),
      size: readU8(),
    });
  }
  return pellets;
}

function readPowerUps(): PowerUpSnapshot[] {
  const count = readCount();
  const powerUps: PowerUpSnapshot[] = [];
  for (let i = 0; i < count; i += 1) {
    powerUps.push({ id: readId(), kind: readString(), x: readVarInt(), y: readVarInt() });
  }
  return powerUps;
}

function readProjectiles(): ProjectileSnapshot[] {
  const count = readCount();
  const projectiles: ProjectileSnapshot[] = [];
  for (let i = 0; i < count; i += 1) {
    projectiles.push({
      id: readId(),
      kind: readString(),
      ownerId: readId(),
      x: readVarInt(),
      y: readVarInt(),
      armed: readU8() !== 0,
    });
  }
  return projectiles;
}

function readSayings(): SayingSnapshot[] {
  const count = readCount();
  const sayings: SayingSnapshot[] = [];
  for (let i = 0; i < count; i += 1) {
    sayings.push({ id: readId(), text: readString(), endsAtTick: readVarUint() });
  }
  return sayings;
}

function readInventory(): PlayerInventory {
  const count = readCount();
  if (count === 0) return { slots: [], selected: 0 };
  const selected = readVarUint();
  const slots: InventorySlotSnapshot[] = [];
  for (let i = 0; i < count; i += 1) {
    slots.push({
      kind: readString(),
      ticksLeft: readVarUint(),
      charges: readVarUint(),
      running: readU8() === 1,
    });
  }
  return { slots, selected };
}

function readOptionalPowerUps(): PowerUpSnapshot[] | null {
  return readU8() === 0 ? null : readPowerUps();
}

function readPrizeShards(): PrizeShardSnapshot[] {
  const count = readCount();
  const shards: PrizeShardSnapshot[] = [];
  for (let i = 0; i < count; i += 1) {
    shards.push({ id: readId(), x: readVarInt(), y: readVarInt() });
  }
  return shards;
}

function readOptionalPrizeShards(): PrizeShardSnapshot[] | null {
  return readU8() === 0 ? null : readPrizeShards();
}

function readStars(): StarSnapshot[] {
  const count = readCount();
  const stars: StarSnapshot[] = [];
  for (let i = 0; i < count; i += 1) {
    stars.push({ id: readId(), x: readVarInt(), y: readVarInt() });
  }
  return stars;
}

function readOptionalStars(): StarSnapshot[] | null {
  return readU8() === 0 ? null : readStars();
}

/**
 * Both sides' rows, or null in a room that is not a `teams` room.
 *
 * A side's id is an index into `TEAM_IDS`, which is two entries and will stay two — see `TeamId`
 * for why a third colour is a different game rather than an append to this tuple.
 */
function readTeams(): { team: (typeof TEAM_IDS)[number]; mass: number; alive: number }[] | null {
  if (readU8() === 0) return null;
  const rows: { team: (typeof TEAM_IDS)[number]; mass: number; alive: number }[] = [];
  for (let i = 0; i < 2; i += 1) {
    const team = TEAM_IDS[readU8()] ?? TEAM_IDS[0];
    rows.push({ team, mass: readVarUint(), alive: readVarUint() });
  }
  return rows;
}

/** The viewer's own side, biased by one — see the writer. */
function readTeamId(): (typeof TEAM_IDS)[number] | null {
  const index = readVarUint();
  return index === 0 ? null : (TEAM_IDS[index - 1] ?? null);
}

/** The presence byte's other half — see `writeOptionalSpikes`. */
function readOptionalSpikes(): SpikeSnapshot[] | null {
  return readU8() === 0 ? null : readSpikes();
}

function readSpikes(): SpikeSnapshot[] {
  const count = readCount();
  const spikes: SpikeSnapshot[] = [];
  for (let i = 0; i < count; i += 1) {
    spikes.push({ id: readId(), x: readVarInt(), y: readVarInt(), fed: readVarUint() });
  }
  return spikes;
}

function readDuels(): DuelSnapshot[] {
  const count = readCount();
  const duels: DuelSnapshot[] = [];
  for (let i = 0; i < count; i += 1) {
    const id = readId();
    const gameId = readString();
    const phase = DUEL_PHASES[readU8()];
    const aId = readId();
    const bId = readId();
    const aLocked = readBool();
    const bLocked = readBool();
    const aChoice = readOptionalString();
    const bChoice = readOptionalString();
    const outcomeIndex = readU8();
    const outcome = outcomeIndex === 0xff ? null : DUEL_OUTCOMES[outcomeIndex];
    const outcomeReason = readDuelOutcomeReason();
    duels.push({
      id,
      gameId,
      // An out-of-range enum index decodes to `undefined`, which the schema refuses — the codec
      // does not validate, it just never lies about what it read.
      phase: phase as DuelSnapshot['phase'],
      aId,
      bId,
      aLocked,
      bLocked,
      aChoice,
      bChoice,
      outcome: outcome as DuelSnapshot['outcome'],
      outcomeReason,
      endsAtTick: readVarUint(),
      stake: readNullableF64(),
      pot: readDuelPot(),
    });
  }
  return duels;
}

/**
 * The reason index back to a reason, and **anything this build has never heard of back to null**.
 *
 * This is the one enum in this file that degrades instead of decoding to `undefined` and letting
 * the schema refuse the frame, and the difference is what a client could do about it. A bad
 * `phase` or `outcome` index means a frame nothing can draw. A reason index means a *sentence*:
 * a client one build behind a server that appended a fourth reason still knows who won and by how
 * much, so it renders the plain win / loss / draw it rendered before the field existed and loses
 * only the explanation. A bundled APK has no OTA route, so that client has to keep playing.
 */
function readDuelOutcomeReason(): DuelSnapshot['outcomeReason'] {
  const index = readU8();
  if (index === DUEL_OUTCOME_REASON_NONE) return null;
  return DUEL_OUTCOME_REASONS[index] ?? null;
}

/**
 * The phase index back to a phase, and **anything this build has never heard of back to
 * `waiting`**.
 *
 * The second enum in this file that degrades instead of decoding to `undefined` and letting the
 * schema refuse the frame, and it is `readDuelOutcomeReason`'s argument at a higher stake: a
 * refused `match` frame is a deathmatch room a stale client cannot read *at all*, for as long as
 * it stays in it, and a bundled APK has no OTA route to fix that with.
 *
 * **IT DEGRADES TO `waiting` BECAUSE THAT IS THE PHASE THAT CLAIMS NOTHING.** A round is not
 * running, nobody has won, and the deadline, the round number and the three counts beside it are
 * all still read and are all still true — so what a stale client loses is a card it could not
 * have drawn anyway, not the frame. It is also the phase a room starts in and the state a client
 * is in before any match frame has arrived, so it is the one reading the HUD is already built
 * for. The two alternatives are worse in the same way and are worth naming: `ended` would assert
 * a winner nothing on the frame names, and `live` would assert a round in progress — and a
 * phase somebody appends later is far likelier to be another between-rounds state (a countdown,
 * an intermission) than a second kind of round.
 */
/** A finished round's scoreboard: a count, then a name, a mass and a flag each. */
function readStandings(): { name: string; mass: number; isBot: boolean }[] {
  const count = readVarUint();
  const rows: { name: string; mass: number; isBot: boolean }[] = [];
  for (let index = 0; index < count; index += 1) {
    rows.push({ name: readString(), mass: readVarUint(), isBot: readBool() });
  }
  return rows;
}

function readMatchPhase(): 'waiting' | 'live' | 'ended' {
  return MATCH_PHASES[readU8()] ?? 'waiting';
}

/**
 * The biased index back to a mode, and **anything this build has never heard of back to absent**.
 *
 * Zero is "this frame did not say", which is what a `welcome` built before `RoomInfo.mode`
 * existed carries, and every reader already takes an absent mode as `classic`. An index past the
 * end lands in the same place deliberately rather than throwing: `welcome` is the one frame a
 * client cannot do without — refusing it is refusing the join — and a third mode read as the
 * classic room this protocol's other thirty-five versions describe is a client that keeps
 * playing. `readDuelOutcomeReason`'s rule, on the frame where it matters most.
 *
 * Omitted rather than set to undefined, for `readLeaderboard`'s reason: a decoded frame has to be
 * deep-equal to the one that was encoded.
 */
function readRoomMode(): (typeof ROOM_MODES)[number] | undefined {
  const index = readVarUint();
  return index === 0 ? undefined : ROOM_MODES[index - 1];
}

/** The sentinel byte back to null; anything else is the percentage it was written as. */
function readDuelPot(): number | null {
  const value = readU8();
  return value === DUEL_POT_ABSENT ? null : value;
}

function readSkins(): PlayerSkin[] {
  const count = readCount();
  const skins: PlayerSkin[] = [];
  for (let i = 0; i < count; i += 1) {
    skins.push({ id: readId(), url: readString() });
  }
  return skins;
}

function readCountries(): PlayerCountry[] {
  const count = readCount();
  const countries: PlayerCountry[] = [];
  for (let i = 0; i < count; i += 1) {
    const id = readId();
    countries.push({ id, country: readCountryCode() });
  }
  return countries;
}

/** The two raw bytes `writeCountries` wrote, back as a string. */
function readCountryCode(): string {
  return String.fromCharCode(readU8(), readU8());
}

function readOptionalCountry(): string | undefined {
  return readU8() === 0 ? undefined : readCountryCode();
}

function readIds(): string[] {
  const count = readCount();
  const ids: string[] = [];
  for (let i = 0; i < count; i += 1) ids.push(readId());
  return ids;
}

function readLeaderboard(): LeaderboardEntry[] {
  const count = readCount();
  const entries: LeaderboardEntry[] = [];
  for (let i = 0; i < count; i += 1) {
    const entry: LeaderboardEntry = {
      rank: readVarUint(),
      playerId: readId(),
      name: readString(),
      mass: readF64(),
    };
    // Omitted rather than set to undefined, so a decoded frame is deep-equal to the one that was
    // encoded — which is what `binary-codec.test.ts` pins every message kind against.
    const country = readOptionalCountry();
    if (country !== undefined) entry.country = country;
    entries.push(entry);
  }
  return entries;
}

function readRoster(): RosterEntry[] {
  const count = readCount();
  const entries: RosterEntry[] = [];
  for (let i = 0; i < count; i += 1) {
    const entry: RosterEntry = {
      id: readId(),
      name: readString(),
      bot: readU8() === 1,
      x: readVarInt(),
      y: readVarInt(),
    };
    // Omitted rather than set to undefined, for `readLeaderboard`'s reason: a decoded frame has
    // to be deep-equal to the one that was encoded.
    const country = readOptionalCountry();
    if (country !== undefined) entry.country = country;
    entries.push(entry);
  }
  return entries;
}

function readNoticeActor(): NoticeActor {
  return { name: readString(), bot: readU8() === 1 };
}

function readNotices(): GameNotice[] {
  const count = readCount();
  const notices: GameNotice[] = [];
  for (let i = 0; i < count; i += 1) {
    const kind = NOTICE_KINDS[readU8()];
    if (kind === undefined) return fail('unknown notice kind');
    const notice: GameNotice = {
      kind: kind as NoticeKind,
      subject: readNoticeActor(),
      target: readU8() === 0 ? null : readNoticeActor(),
    };
    // Omitted rather than set to undefined, for `readRoster`'s reason: a decoded frame has to be
    // deep-equal to the one that was encoded.
    const powerUp = readOptionalString();
    if (powerUp !== null) notice.powerUp = powerUp;
    const text = readOptionalString();
    if (text !== null) notice.text = text;
    notices.push(notice);
  }
  return notices;
}

function readVoteOptions(): VoteOption[] {
  const count = readCount();
  const options: VoteOption[] = [];
  for (let i = 0; i < count; i += 1) options.push({ id: readString(), label: readString() });
  return options;
}

function readVoteResult(): VoteResultSnapshot | null {
  if (readU8() === 0) return null;
  const winner = readOptionalString();
  const count = readCount();
  const tallies: VoteResultSnapshot['tallies'] = [];
  for (let i = 0; i < count; i += 1) {
    tallies.push({ optionId: readString(), votes: readVarUint() });
  }
  const massMoved = readF64();
  const outcome = readU8();
  return {
    winner,
    tallies,
    massMoved,
    starOutcome: outcome === STAR_OUTCOME_NONE ? null : (STAR_OUTCOMES[outcome] ?? null),
  };
}

function readVoteSelf(): VoteSelfSnapshot | null {
  if (readU8() === 0) return null;
  return { choice: readString(), massChange: readF64() };
}

function readLeaderboardSelf(): LeaderboardSelf | null {
  if (readU8() === 0) return null;
  return { rank: readVarUint(), mass: readF64() };
}

function readMessage(): ClientMessage | ServerMessage {
  const kind = readU8();
  switch (kind) {
    case KIND_HELLO: {
      const protocolVersion = readVarUint();
      const name = readString();
      const ticket = readOptionalString();
      const roomId = readU8() === 0 ? undefined : readId();
      const aspect = readOptionalF64();
      const hello: Record<string, unknown> = { t: 'hello', protocolVersion, name };
      if (ticket !== null) hello.ticket = ticket;
      if (roomId !== undefined) hello.roomId = roomId;
      if (aspect !== undefined) hello.aspect = aspect;
      return hello as unknown as ClientMessage;
    }
    case KIND_INPUT: {
      const seq = readVarUint();
      const dirX = readF64();
      const dirY = readF64();
      const boostBits = readU8();
      const splitBits = readU8();
      const ejectBits = readU8();
      const usePowerUpBits = readU8();
      const duelChoice = readOptionalString();
      const aspect = readOptionalF64();
      const saying = readOptionalString();
      const camX = readOptionalF64();
      const camY = readOptionalF64();
      const selectSlot = readVarUint();
      const voteAnswer = readOptionalString();
      const sprintBits = readU8();
      const endRoundBits = readU8();
      const input: Record<string, unknown> = { t: 'input', seq, dirX, dirY };
      if ((boostBits & 1) !== 0) input.boost = (boostBits & 2) !== 0;
      if ((splitBits & 1) !== 0) input.split = (splitBits & 2) !== 0;
      if ((ejectBits & 1) !== 0) input.eject = (ejectBits & 2) !== 0;
      if ((usePowerUpBits & 1) !== 0) input.usePowerUp = (usePowerUpBits & 2) !== 0;
      if (duelChoice !== null) input.duelChoice = duelChoice;
      if (aspect !== undefined) input.aspect = aspect;
      if (saying !== null) input.saying = saying;
      if (camX !== undefined) input.camX = camX;
      if (camY !== undefined) input.camY = camY;
      if (selectSlot !== 0) input.selectSlot = selectSlot - 1;
      if (voteAnswer !== null) input.voteAnswer = voteAnswer;
      if ((sprintBits & 1) !== 0) input.sprint = (sprintBits & 2) !== 0;
      if ((endRoundBits & 1) !== 0) input.endRound = (endRoundBits & 2) !== 0;
      return input as unknown as ClientMessage;
    }
    case KIND_PING:
      return { t: 'ping', clientTime: readVarUint() };
    case KIND_WELCOME: {
      const protocolVersion = readVarUint();
      const playerId = readId();
      const room: RoomInfo = {
        id: readId(),
        name: readString(),
        worldWidth: readF64(),
        worldHeight: readF64(),
        tickRateHz: readF64(),
        maxPlayers: readVarUint(),
        playerCount: readVarUint(),
      };
      // Read in the order it was written — the end of the room block — and omitted rather than
      // set to undefined when the frame did not say, for `readLeaderboard`'s reason.
      const mode = readRoomMode();
      if (mode !== undefined) room.mode = mode;
      const serverTime = readVarUint();
      return {
        t: 'welcome',
        protocolVersion,
        playerId,
        room,
        serverTime,
        spectator: readU8() === 1,
      };
    }
    case KIND_SNAPSHOT:
      return {
        t: 'snapshot',
        tick: readVarUint(),
        ackSeq: readVarUint(),
        players: readPlayers(),
        pellets: readPellets(),
        spikes: readSpikes(),
        duels: readDuels(),
        skins: readSkins(),
        bots: readIds(),
        countries: readCountries(),
        powerUps: readPowerUps(),
        inventory: readInventory(),
        projectiles: readProjectiles(),
        sayings: readSayings(),
        merges: readMerges(),
        frozen: readIds(),
        slowed: readIds(),
        invisible: readIds(),
        spawnProtected: readIds(),
        prizeShards: readPrizeShards(),
        stars: readStars(),
        sprinting: readIds(),
        stamina: readVarUint(),
      };
    case KIND_DELTA:
      return {
        t: 'delta',
        tick: readVarUint(),
        baseTick: readVarUint(),
        ackSeq: readVarUint(),
        players: readPlayers(),
        removedPlayers: readIds(),
        pelletsAdded: readPellets(),
        pelletsRemoved: readIds(),
        duels: readDuels(),
        skins: readSkins(),
        bots: readIds(),
        countries: readCountries(),
        powerUps: readOptionalPowerUps(),
        inventory: readInventory(),
        projectiles: readProjectiles(),
        sayings: readSayings(),
        merges: readMerges(),
        frozen: readIds(),
        slowed: readIds(),
        invisible: readIds(),
        spawnProtected: readIds(),
        prizeShards: readOptionalPrizeShards(),
        stars: readOptionalStars(),
        sprinting: readIds(),
        stamina: readVarUint(),
        spikes: readOptionalSpikes(),
      };
    case KIND_LEADERBOARD:
      return {
        t: 'leaderboard',
        tick: readVarUint(),
        humanCount: readVarUint(),
        botCount: readVarUint(),
        entries: readLeaderboard(),
        self: readLeaderboardSelf(),
      };
    case KIND_ROSTER:
      return { t: 'roster', tick: readVarUint(), entries: readRoster() };
    case KIND_NOTICE:
      return { t: 'notice', tick: readVarUint(), notices: readNotices() };
    case KIND_VOTE:
      return {
        t: 'vote',
        tick: readVarUint(),
        id: readId(),
        prompt: readString(),
        options: readVoteOptions(),
        endsAtTick: readVarUint(),
        result: readVoteResult(),
        self: readVoteSelf(),
        star: readU8() === 1,
      };
    case KIND_MATCH: {
      const tick = readVarUint();
      const phase = readMatchPhase();
      const endsAtTick = readVarUint();
      return {
        t: 'match',
        tick,
        phase,
        // The bias undone: zero is the phase with no deadline. See `serverMatchSchema`.
        endsAtTick: endsAtTick === 0 ? null : endsAtTick - 1,
        round: readVarUint(),
        waiting: readVarUint(),
        capacity: readVarUint(),
        alive: readVarUint(),
        humansAlive: readVarUint(),
        winnerName: readOptionalString(),
        winnerIsBot: readBool(),
        // Read positionally, so the object literal's own field order IS the wire order — the rule
        // every other field on this frame is under.
        standings: readStandings(),
        youWon: readBool(),
        // Read positionally, so the object literal's own field order IS the wire order.
        teams: readTeams(),
        yourTeam: readTeamId(),
      };
    }
    case KIND_DIED: {
      const tick = readVarUint();
      const killerId = readU8() === 0 ? null : readId();
      const killerName = readOptionalString();
      const killerIsBot = readU8() === 1;
      return {
        t: 'died',
        tick,
        killerId,
        killerName,
        killerIsBot,
        mass: readF64(),
        survivedMs: readVarUint(),
      };
    }
    case KIND_PONG:
      return { t: 'pong', clientTime: readVarUint(), serverTime: readVarUint() };
    case KIND_ERROR: {
      const index = readU8();
      const code = index === ERROR_CODE_RAW ? readString() : PROTOCOL_ERROR_CODES[index];
      return { t: 'error', code: code as ProtocolErrorCode, message: readString() };
    }
    default:
      return fail(`unknown frame kind ${kind}`);
  }
}

export function decodeBinary(data: Uint8Array<ArrayBufferLike>): unknown {
  source = data;
  // Rebound only when the frame arrives in a different backing store. `ws` hands out `Buffer`s
  // carved from one pooled ArrayBuffer, so the common case is a hit and a decode allocates no
  // view at all.
  if (data.buffer !== readerBuffer) {
    readerBuffer = data.buffer;
    readerView = new DataView(data.buffer);
  }
  position = 0;
  const message = readMessage();
  // Trailing bytes mean the two sides disagree about the shape of this frame, which is exactly
  // the failure `PROTOCOL_VERSION` exists to make loud. Better a refused frame than a decode
  // that silently read the wrong fields.
  if (position !== source.length) fail('frame has trailing bytes');
  return message;
}

function asBytes(data: ArrayBuffer | Uint8Array): Uint8Array {
  return data instanceof Uint8Array ? data : new Uint8Array(data);
}

/**
 * Decode a frame of either format — see the header note on why decoding is codec-independent.
 *
 * The sniff is total: a text frame is JSON by definition, and a binary frame's first byte is a
 * frame kind, every one of which is below `{` (0x7B). There is no byte sequence both branches
 * would accept.
 */
export function decodeFrame(data: WireData): unknown {
  if (typeof data === 'string') return JSON.parse(data);
  const bytes = asBytes(data);
  if (bytes.length === 0) throw new Error('binary protocol: empty frame');
  if (bytes[0] >= KIND_COUNT) return JSON.parse(toText(bytes));
  return decodeBinary(bytes);
}

export const binaryCodec: ProtocolCodec = {
  name: 'binary',
  encode: encodeBinary,
  decode: decodeFrame,
};
